import DetailsView from "@/components/resume/detail/DetailsView";
import SkillsView from "@/components/resume/skill/SkillsView";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DetailData, EducationResponse, SkillData } from '@/lib/api';
import { formatProfileMonth, monthInputToApiDate } from '@/lib/profile-dates';
import { Pencil, Save, Trash2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import MonthYearSelect from '../MonthYearSelect';

interface EducationViewProps {
  education: EducationResponse;
  details?: DetailData[];
  skills?: SkillData[];
  onDelete?: (id?: string) => void;
  onUpdate?: (education: EducationResponse) => Promise<void> | void;
  onDetailsChange?: (details: DetailData[]) => void;
  loading?: boolean;
}

const EducationView: React.FC<EducationViewProps> = ({ education, details, skills, onDelete, onUpdate, onDetailsChange, loading }) => {
  const [actionsOpen, setActionsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftEducation, setDraftEducation] = useState<EducationResponse>(education);

  useEffect(() => {
    if (!isEditing) setDraftEducation(education);
  }, [education, isEditing]);

  const startEditing = () => {
    setDraftEducation(education);
    setIsEditing(true);
    setActionsOpen(true);
  };

  const cancelEditing = () => {
    setDraftEducation(education);
    setIsEditing(false);
    setActionsOpen(false);
  };

  const saveEducation = async () => {
    await onUpdate?.(draftEducation);
    setIsEditing(false);
    setActionsOpen(false);
  };

  const updateDraft = (field: keyof EducationResponse, value: string | boolean | null) => {
    setDraftEducation(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="relative border-l-2 border-gray-200 pl-4 sm:pl-6 pb-6 group">
      <div className="absolute w-3 h-3 bg-interview-blue rounded-full -left-[7px] top-[10px]"></div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-4">
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input
                value={draftEducation.institution}
                onChange={e => updateDraft('institution', e.target.value)}
                placeholder="Institution"
                disabled={loading}
              />
              <Input
                value={draftEducation.degree_type}
                onChange={e => updateDraft('degree_type', e.target.value)}
                placeholder="Degree"
                disabled={loading}
              />
              <Input
                value={draftEducation.major}
                onChange={e => updateDraft('major', e.target.value)}
                placeholder="Field of study"
                disabled={loading}
              />
              <MonthYearSelect
                value={draftEducation.start_date}
                onChange={value => updateDraft('start_date', monthInputToApiDate(value))}
                disabled={loading}
              />
              <MonthYearSelect
                value={draftEducation.end_date}
                onChange={value => updateDraft('end_date', monthInputToApiDate(value) || null)}
                disabled={loading || draftEducation.is_ongoing}
              />
              <label className="flex items-center gap-2 text-sm text-gray-600 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(draftEducation.is_ongoing)}
                  onChange={e => {
                    updateDraft('is_ongoing', e.target.checked);
                    if (e.target.checked) updateDraft('end_date', null);
                  }}
                  disabled={loading}
                  className="h-4 w-4 rounded border-gray-300 text-interview-blue focus:ring-interview-blue"
                />
                Currently studying here
              </label>
            </div>
          ) : (education.degree_type || education.major) ? (
            <>
              <h4 className="text-base sm:text-lg font-semibold text-interview-blue break-words">
                {education.degree_type}{education.degree_type && education.major && ' in '}{education.major}
              </h4>
              <h5 className="text-sm sm:text-base font-medium mt-1 break-words">{education.institution || 'Unknown Institution'}</h5>
            </>
          ) : (
            <h4 className="text-base sm:text-lg font-semibold text-interview-blue break-words">
              {education.institution || 'Unknown Institution'}
            </h4>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs sm:text-sm text-gray-600 sm:whitespace-nowrap">
            {formatProfileMonth(education.start_date)}{formatProfileMonth(education.start_date) && ' - '}{education.is_ongoing ? 'Present' : formatProfileMonth(education.end_date)}
            {education.is_ongoing && (
              <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-200 text-xs">
                Current
              </Badge>
            )}
          </div>
          {onDelete && (
            <div className="flex items-center gap-1">
              {actionsOpen ? (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={saveEducation}
                    disabled={loading || !draftEducation.institution || !draftEducation.degree_type || !draftEducation.major || !draftEducation.start_date}
                    aria-label="Save education"
                  >
                    <Save className="h-4 w-4 text-green-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={cancelEditing}
                    disabled={loading}
                    aria-label="Cancel education edit"
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onDelete(education.id)}
                    disabled={loading}
                    aria-label="Delete education"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={startEditing}
                  disabled={loading}
                  aria-label="Edit education"
                >
                  <Pencil className="h-4 w-4 text-interview-blue" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      {education.gpa && <p className="text-xs sm:text-sm text-gray-500 mt-1">GPA: {education.gpa}</p>}
      {education.description && <p className="text-xs sm:text-sm text-gray-700 mt-2 whitespace-pre-line">{education.description}</p>}

      {/* Details & Achievements */}
      <h5 className="text-sm sm:text-md font-semibold mt-4 mb-2">Details & Achievements</h5>
      <DetailsView educationId={education.id} details={details} onDetailsChange={onDetailsChange} />

      {/* Skills */}
      <h5 className="text-sm sm:text-md font-semibold mt-4 mb-2">Skills</h5>
      <SkillsView educationId={education.id} skills={skills} />
    </div>
  )
};

export default EducationView;
